import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl, fmtMoney } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { usePageMeta } from "@/lib/seo";
import { toast } from "sonner";
import { ShieldCheck, Plus } from "lucide-react";

// Fixed spec sheet from the Qlife wholesale list — keyed by store SKU.
const SPECS = {
  "QLIFE-EB41":      { group: "Fat Tire", motor: "1500W", battery: "48V 13Ah", tires: '16x4.0" fat', brakes: "Mechanical", susp: "Full", ul: false },
  "QLIFE-EB41PLUS":  { group: "Fat Tire", motor: "750W", battery: "48V 15.6Ah", tires: '16x4.0" fat', brakes: "Mechanical", susp: "Full", ul: true },
  "QLIFE-EB42":      { group: "Fat Tire", motor: "750W", battery: "48V 15.6Ah", tires: '20x4.0" fat', brakes: "Mechanical", susp: "Full", ul: true },
  "QLIFE-EB42DUAL":  { group: "Fat Tire", motor: "750W", battery: "Dual 48V 15.6Ah", tires: '20x4.0" fat', brakes: "Mechanical", susp: "Full", ul: true },
  "QLIFE-EB42PLUS":  { group: "Fat Tire", motor: "1000W", battery: "52V 20Ah", tires: '20x4.0" fat', brakes: "Hydraulic", susp: "Full", ul: false },
  "QLIFE-EB42MAX":   { group: "Fat Tire", motor: "AWD 750W+500W", battery: "Dual 52V 20Ah", tires: '20x4.0" fat', brakes: "Hydraulic", susp: "Full", ul: false },
  "QLIFE-EB13":      { group: "All-Terrain", motor: "750W", battery: "48V 15.6Ah", tires: '26x4.0" fat', brakes: "Mechanical", susp: "Front", ul: true },
  "QLIFE-EB13PLUS":  { group: "All-Terrain", motor: "750W", battery: "48V 20Ah", tires: '26x4.0" fat', brakes: "Mechanical", susp: "Full", ul: false },
  "QLIFE-EB13MAX":   { group: "All-Terrain", motor: "AWD 750W+500W", battery: "52V 20Ah", tires: '26x4.0" fat', brakes: "Hydraulic", susp: "Full", ul: false },
  "QLIFE-EB50":      { group: "Fat Tire", motor: "500W", battery: "48V 10.4Ah", tires: '20x4.0" fat', brakes: "Mechanical", susp: "Full", ul: true },
  "QLIFE-EB50PLUS":  { group: "Fat Tire", motor: "750W", battery: "48V 15.6Ah", tires: '20x4.0" fat', brakes: "Mechanical", susp: "Full", ul: true },
  "QLIFE-EB16PLUS":  { group: "Commuter", motor: "350W", battery: "48V 7.8Ah", tires: '26x2.10"', brakes: "Mechanical", susp: "Front", ul: true },
  "QLIFE-EB30":      { group: "Commuter", motor: "500W", battery: "48V 10.4Ah", tires: '26x2.10"', brakes: "Mechanical", susp: "Front", ul: true },
  "QLIFE-EB69":      { group: "Commuter", motor: "500W", battery: "48V 15Ah", tires: '26x2.10"', brakes: "Mechanical", susp: "Front", ul: false },
  "QLIFE-EB27":      { group: "Folding", motor: "500W", battery: "48V 13.5Ah", tires: '20x3.0"', brakes: "Mechanical", susp: "Full", ul: true },
  "QLIFE-EB27PLUS":  { group: "Folding", motor: "750W", battery: "48V 20Ah", tires: '20x4.0" fat', brakes: "Mechanical", susp: "Full", ul: false },
  "QLIFE-EB20-26P":  { group: "Mountain", motor: "350W", battery: "48V 7.8Ah", tires: '26x2.1" MTB', brakes: "Mechanical", susp: "Full", ul: true },
  "QLIFE-EB20-26F":  { group: "Mountain", motor: "350W", battery: "48V 7.8Ah", tires: '26x2.1" MTB', brakes: "Mechanical", susp: "Full", ul: true },
  "QLIFE-EB20-27.5": { group: "Mountain", motor: "500W", battery: "48V 10.4Ah", tires: '27.5x2.1" MTB', brakes: "Mechanical", susp: "Full", ul: true },
  "QLIFE-EB20-27.5F":{ group: "Mountain", motor: "500W", battery: "48V 10.4Ah", tires: '27.5x2.1" MTB', brakes: "Mechanical", susp: "Full", ul: true },
  "QLIFE-EB70":      { group: "Dirt Bike", motor: "1500W", battery: "48V 30Ah", tires: '19"F / 17"R dirt', brakes: "Hydraulic", susp: "Full", ul: false },
  "QLIFE-EB76":      { group: "Dirt Bike", motor: "1500W (3500W peak)", battery: "48V 25Ah", tires: '14"F / 12"R', brakes: "Hydraulic", susp: "Full", ul: false },
  "QLIFE-EB58PLUS":  { group: "Trike", motor: "350W", battery: "48V 13Ah", tires: '18x3.0" trike', brakes: "Mechanical", susp: "Front & rear", ul: false },
  "QLIFE-EB11":      { group: "Trike", motor: "500W mid-drive", battery: "48V 13Ah", tires: '20x4.0" fat trike', brakes: "Mechanical", susp: "Rear basket", ul: false },
};

const GROUPS = ["All", "Fat Tire", "All-Terrain", "Commuter", "Folding", "Mountain", "Dirt Bike", "Trike"];

export default function EbikeComparePage() {
  usePageMeta({
    title: "Compare Qlife E-Bikes — Motor, Battery & Price | IPVALUE Marketplace",
    description: "Compare all 24 Qlife electric bike models side by side — motor power, battery, tires, brakes and price. Ships to you brand-new in box in about 5 business days.",
  });
  const { addItem } = useCart();
  const [bikes, setBikes] = useState([]);
  const [group, setGroup] = useState("All");

  useEffect(() => {
    api
      .get("/products", { params: { category: "E-Bikes", limit: 60 } })
      .then((r) => {
        const items = (r.data.items || r.data || []).filter((p) => SPECS[(p.sku || "").toUpperCase()]);
        items.sort((a, b) => a.price - b.price);
        setBikes(items);
      })
      .catch(() => {});
  }, []);

  const rows = useMemo(
    () => bikes.filter((p) => group === "All" || SPECS[p.sku.toUpperCase()].group === group),
    [bikes, group]
  );

  return (
    <div className="bg-white" data-testid="ebike-compare-page">
      <div className="bg-[#0A1C40] text-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-8 py-12">
          <div className="inline-flex items-center gap-2 bg-[#FFC220] text-[#0A1C40] text-[10px] font-black tracking-[0.2em] uppercase px-3 py-1.5">
            Qlife E-Bikes · Compare
          </div>
          <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter mt-3">
            Find your ride, <span className="text-[#FFC220]">spec by spec.</span>
          </h1>
          <p className="text-white/70 mt-3 max-w-2xl text-sm md:text-base">
            Every model ships to you brand-new in box in about 5 business days, with a
            1-year warranty on battery, motor, controller and frame.
          </p>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-8">
        <div className="flex gap-2 overflow-x-auto pb-3 [scrollbar-width:none]" data-testid="compare-group-tabs">
          {GROUPS.map((g) => (
            <button
              key={g}
              onClick={() => setGroup(g)}
              className={`shrink-0 px-4 py-2 rounded-full text-sm font-bold border-2 transition-colors ${
                group === g
                  ? "bg-[#0A1C40] text-white border-[#0A1C40]"
                  : "border-gray-300 text-gray-700 hover:border-[#0A1C40]"
              }`}
              data-testid={`compare-tab-${g.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {g}
            </button>
          ))}
        </div>

        <div className="mt-4 border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]" data-testid="compare-table">
            <thead className="bg-[#FAFAFA] border-b-2 border-[#0A1C40] text-left">
              <tr className="ipv-eyebrow">
                <th className="p-3">Model</th>
                <th className="p-3">Motor</th>
                <th className="p-3">Battery</th>
                <th className="p-3">Tires</th>
                <th className="p-3">Brakes</th>
                <th className="p-3">Suspension</th>
                <th className="p-3">UL</th>
                <th className="p-3 text-right">Price</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((p) => {
                const s = SPECS[p.sku.toUpperCase()];
                return (
                  <tr key={p.id} className="hover:bg-blue-50/40" data-testid={`compare-row-${p.sku}`}>
                    <td className="p-3">
                      <Link to={`/product/${p.slug}`} className="flex items-center gap-3 group">
                        <div className="w-14 h-14 bg-gray-100 shrink-0 overflow-hidden">
                          {p.images?.[0] && (
                            <img src={fileUrl(p.images[0])} alt={p.name} className="w-full h-full object-cover" />
                          )}
                        </div>
                        <div>
                          <div className="font-bold text-[#0A1C40] group-hover:underline leading-tight">
                            {p.name.replace("Qlife ", "").replace(" Electric Bike", "").split(" — ")[0]}
                          </div>
                          <div className="text-[10px] text-gray-500 uppercase tracking-wider">
                            {s.group} · ships in ~5 days
                          </div>
                        </div>
                      </Link>
                    </td>
                    <td className="p-3 font-semibold">{s.motor}</td>
                    <td className="p-3">{s.battery}</td>
                    <td className="p-3">{s.tires}</td>
                    <td className="p-3">{s.brakes}</td>
                    <td className="p-3">{s.susp}</td>
                    <td className="p-3">
                      {s.ul ? <ShieldCheck className="w-4 h-4 text-green-700" title="UL certified" /> : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="p-3 text-right font-display font-black text-lg text-[#0A1C40]">
                      {fmtMoney(p.price)}
                    </td>
                    <td className="p-3">
                      <button
                        onClick={() => {
                          addItem(p, 1);
                          toast.success(`${p.name.split(" — ")[0]} added to cart`);
                        }}
                        className="inline-flex items-center gap-1 bg-[#FFC220] text-[#0A1C40] text-xs font-black px-3 py-2 rounded-full hover:bg-[#0A1C40] hover:text-[#FFC220] transition-colors"
                        data-testid={`compare-add-${p.sku}`}
                      >
                        <Plus className="w-3 h-3" /> Add
                      </button>
                    </td>
                  </tr>
                );
              })}
              {!rows.length && (
                <tr>
                  <td colSpan={9} className="p-8 text-center text-gray-500">Loading models…</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-gray-500 mt-3">
          UL = UL-certified electrical system. All prices include free delivery to our store — pickup
          or local delivery options at checkout.
        </p>
      </div>
    </div>
  );
}
